/**
 * The three controls the Calculator is built from, and the reason it has three rather than none.
 *
 * The screen it replaces used the platform's own: two `<select>`s, three `<input type=number>`s
 * and five checkboxes. Each of those is under 44px on a phone, each opens a different piece of OS
 * furniture, and a number input on iOS puts a full keyboard over the answer you were reading in
 * order to change a value between 1 and 20. Eighteen tap targets under the line, on a screen
 * whose whole job is to be used one-handed in a bar in August.
 *
 * So: a segmented control instead of a select, a stepper instead of a number field, a toggle row
 * instead of a checkbox. All three are ordinary buttons, all three are 44px on a phone and 40 on
 * a desktop, and none of them summons a keyboard. They are here rather than in the screen because
 * the Farm and Declare briefs reach for the same vocabulary and a second copy would drift.
 */

import type { ReactNode } from 'react';

/* ------------------------------------------------------------------------------- segmented */

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Four choices, all visible, one tap each.
 *
 * A `<select>` hides three of four options behind a tap and a system sheet, which is a fair trade
 * when there are thirty and a bad one when there are four — a manager choosing how a player was
 * acquired is comparing the four, not looking one up.
 */
export function Segmented<T extends string>(
  { name, value, options, onChange }:
  { name: string; value: T; options: ReadonlyArray<SegmentOption<T>>; onChange: (v: T) => void },
) {
  return (
    <div className="kcseg" role="radiogroup" aria-label={name}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value}
          className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------------- stepper */

/**
 * Minus, the value, plus — and the value is not a field.
 *
 * The bounds are the caller's, and every caller reads them off the rules engine rather than
 * naming a number (see `domain/calculator.ts`). A stepper that has reached its limit disables
 * that half only; disabling the whole control would hide the value, which is the part being read.
 *
 * `aria-label` on each half rather than a shared one, because "increase" on its own does not say
 * increase WHAT when a screen has three steppers on it.
 */
export function Stepper(
  { label, value, min, max, onChange, down, up, wide }:
  {
    label: string;
    value: number | string;
    min: number;
    max: number;
    /** Called with the delta, not the result — the clamp lives with the bounds. */
    onChange: (delta: -1 | 1) => void;
    /** What the minus button does, in words: "Lower the round". */
    down: string;
    up: string;
    /** True when `value` is a number to compare against the bounds. */
    wide?: boolean;
  },
) {
  const n = typeof value === 'number' ? value : Number.NaN;
  return (
    <div className={`kcstep${wide ? ' wide' : ''}`}>
      <button type="button" aria-label={down} disabled={n <= min} onClick={() => onChange(-1)}>
        &minus;
      </button>
      <output aria-label={label}>{value}</output>
      <button type="button" aria-label={up} disabled={n >= max} onClick={() => onChange(1)}>
        +
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------------------ toggle row */

/**
 * The whole row is the button, and that is the point.
 *
 * A checkbox is 13px of target with a label beside it that does nothing; this is 44px of target
 * with the switch at the end as the readout. Same information, same two states, a tap area a
 * finger can find without looking.
 */
export function ToggleRow(
  { label, on, onChange, last }:
  { label: ReactNode; on: boolean; onChange: (next: boolean) => void; last?: boolean },
) {
  return (
    <button type="button" role="switch" aria-checked={on}
      className={`kctog${last ? ' last' : ''}`} onClick={() => onChange(!on)}>
      <span className="lab">{label}</span>
      <span className={`kctrack${on ? ' on' : ''}`} aria-hidden="true"><i /></span>
    </button>
  );
}

/* ------------------------------------------------------------------------------ field pair */

/** A label above a control. One shape, so three controls line up on a row. */
export function Field(
  { label, className, children }:
  { label?: string; className?: string; children: ReactNode },
) {
  return (
    <div className={`kcfield${className ? ` ${className}` : ''}`}>
      {label && <span className="kclab">{label}</span>}
      {children}
    </div>
  );
}
